import "@/App.css";
import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { CartProvider } from "@/context/CartContext";
import { WishlistProvider } from "@/context/WishlistContext";
import { B2BProvider } from "@/context/B2BContext";
import { LocaleProvider } from "@/context/LocaleContext";
import { AdminAuthProvider } from "@/context/AdminAuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import AnnouncementBar from "@/components/AnnouncementBar";
import CookieConsent from "@/components/CookieConsent";
import CartDrawer from "@/components/CartDrawer";
import { WelcomePopup } from "@/components/WelcomePopup";
import Analytics from "@/components/Analytics";
import { previewSession } from "@/theme/useThemeConfig";
import AdminProtectedRoute from "@/pages/admin/AdminProtectedRoute";
import { useB2B } from "@/context/B2BContext";

const Home = lazy(() => import("@/pages/Home"));
const Shop = lazy(() => import("@/pages/Shop"));
const ProductDetail = lazy(() => import("@/pages/ProductDetail"));
const Cart = lazy(() => import("@/pages/Cart"));
const CheckoutSuccess = lazy(() => import("@/pages/CheckoutSuccess"));
const CheckoutCancel = lazy(() => import("@/pages/CheckoutCancel"));
const Histoire = lazy(() => import("@/pages/Histoire"));
const Artisanat = lazy(() => import("@/pages/Artisanat"));
const Lookbook = lazy(() => import("@/pages/Lookbook"));
const Journal = lazy(() => import("@/pages/Journal"));
const Wishlist = lazy(() => import("@/pages/Wishlist"));
const B2BLogin = lazy(() => import("@/pages/pro/B2BLogin"));
const B2BResetPassword = lazy(() => import("@/pages/pro/B2BResetPassword"));
const B2BLayout = lazy(() => import("@/pages/pro/B2BLayout"));
const B2BCatalog = lazy(() => import("@/pages/pro/B2BCatalog"));
const B2BAccount = lazy(() => import("@/pages/pro/B2BAccount"));
const B2BOrders = lazy(() => import("@/pages/pro/B2BOrders"));
const JournalDetail = lazy(() => import("@/pages/JournalDetail"));
const Ateliers = lazy(() => import("@/pages/Ateliers"));
const AtelierDetail = lazy(() => import("@/pages/AtelierDetail"));
const Partenariats = lazy(() => import("@/pages/Partenariats"));
const Contact = lazy(() => import("@/pages/Contact"));
const Faq = lazy(() => import("@/pages/Faq"));
const Livraison = lazy(() => import("@/pages/Livraison"));
const OrderTracking = lazy(() => import("@/pages/OrderTracking"));
const MentionsLegales = lazy(() => import("@/pages/legal/MentionsLegales"));
const Confidentialite = lazy(() => import("@/pages/legal/Confidentialite"));
const CookiesPage = lazy(() => import("@/pages/legal/CookiesPage"));
const Cgv = lazy(() => import("@/pages/legal/Cgv"));
const NotFound = lazy(() => import("@/pages/NotFound"));

const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminResetPassword = lazy(() => import("@/pages/admin/AdminResetPassword"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminProducts = lazy(() => import("@/pages/admin/AdminProducts"));
const AdminCategories = lazy(() => import("@/pages/admin/AdminCategories"));
const AdminMedia = lazy(() => import("@/pages/admin/AdminMedia"));
const AdminImport = lazy(() => import("@/pages/admin/AdminImport"));
const AdminOrders = lazy(() => import("@/pages/admin/AdminOrders"));
const AdminMarketing = lazy(() => import("@/pages/admin/AdminMarketing"));
const AdminReviews = lazy(() => import("@/pages/admin/AdminReviews"));
const AdminLookbook = lazy(() => import("@/pages/admin/AdminLookbook"));
const AdminContent = lazy(() => import("@/pages/admin/AdminContent"));
const AdminJournal = lazy(() => import("@/pages/admin/AdminJournal"));
const AdminCrm = lazy(() => import("@/pages/admin/AdminCrm"));
const AdminSettings = lazy(() => import("@/pages/admin/AdminSettings"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminB2B = lazy(() => import("@/pages/admin/AdminB2B"));
const AdminTheme = lazy(() => import("@/pages/admin/AdminTheme"));

function PublicLayout({ children }) {
  const { account, loading: b2bLoading } = useB2B();
  const themePreview = !!previewSession();
  return (
    <div className="flex flex-col bg-levami-creme">
      <AnnouncementBar />
      <Header />
      <main id="main-content" className="flex-1 min-h-[40vh]">
        {children}
      </main>
      <Footer />
      {!themePreview && <CookieConsent />}
      {!themePreview && !account && !b2bLoading && <CartDrawer />}
      {!themePreview && !account && !b2bLoading && <WelcomePopup />}
      {!themePreview && <Analytics />}
    </div>
  );
}

function RetailCheckoutRoute({ children }) {
  const { account, loading } = useB2B();
  if (loading) return <LoadingFallback />;
  if (account) return <Navigate to="/pro/catalogue" replace />;
  return <PublicLayout>{children}</PublicLayout>;
}

function ScrollToTop() {
  const location = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  return null;
}

function LoadingFallback() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <p className="text-levami-brun/50 text-sm tracking-label uppercase">Chargement...</p>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <LocaleProvider>
          <B2BProvider>
            <CartProvider>
              <WishlistProvider>
                <AdminAuthProvider>
                  <Suspense fallback={<LoadingFallback />}>
                    <Routes>
                  {/* Admin routes (separate visual system, no public header/footer) */}
                  <Route path="/admin/connexion" element={<AdminLogin />} />
                  <Route path="/admin/reinitialiser" element={<AdminResetPassword />} />
                  <Route
                    path="/admin"
                    element={
                      <AdminProtectedRoute>
                        <AdminLayout />
                      </AdminProtectedRoute>
                    }
                  >
                    <Route index element={<AdminDashboard />} />
                    <Route path="produits" element={<AdminProducts />} />
                    <Route path="categories" element={<AdminCategories />} />
                    <Route path="medias" element={<AdminMedia />} />
                    <Route path="import" element={<AdminImport />} />
                    <Route path="commandes" element={<AdminOrders />} />
                    <Route path="marketing" element={<AdminMarketing />} />
                    <Route path="avis" element={<AdminReviews />} />
                    <Route path="lookbook" element={<AdminLookbook />} />
                    <Route path="contenu" element={<AdminContent />} />
                    <Route path="theme" element={<AdminTheme />} />
                    <Route path="journal" element={<AdminJournal />} />
                    <Route path="crm" element={<AdminCrm />} />
                    <Route path="reglages" element={<AdminSettings />} />
                    <Route path="utilisateurs" element={<AdminUsers />} />
                    <Route path="b2b" element={<AdminB2B />} />
                  </Route>

                  {/* Public storefront */}
                  <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
                  <Route path="/boutique" element={<PublicLayout><Shop /></PublicLayout>} />
                  <Route path="/boutique/:categorySlug" element={<PublicLayout><Shop /></PublicLayout>} />
                  <Route path="/produit/:slug" element={<PublicLayout><ProductDetail /></PublicLayout>} />
                  <Route path="/histoire" element={<PublicLayout><Histoire /></PublicLayout>} />
                  <Route path="/artisanat" element={<PublicLayout><Artisanat /></PublicLayout>} />
                  <Route path="/lookbook" element={<PublicLayout><Lookbook /></PublicLayout>} />
                  <Route path="/journal" element={<PublicLayout><Journal /></PublicLayout>} />
                  <Route path="/favoris" element={<PublicLayout><Wishlist /></PublicLayout>} />
                  <Route path="/pro" element={<B2BLogin />} />
                  <Route path="/pro/reinitialiser" element={<B2BResetPassword />} />
                  <Route element={<B2BLayout />}>
                    <Route path="/pro/catalogue" element={<B2BCatalog />} />
                    <Route path="/pro/panier" element={<B2BCatalog />} />
                    <Route path="/pro/compte" element={<B2BAccount />} />
                    <Route path="/pro/commandes" element={<B2BOrders />} />
                  </Route>
                  <Route path="/journal/:slug" element={<PublicLayout><JournalDetail /></PublicLayout>} />
                  <Route path="/ateliers" element={<PublicLayout><Ateliers /></PublicLayout>} />
                  <Route path="/ateliers/:slug" element={<PublicLayout><AtelierDetail /></PublicLayout>} />
                  <Route path="/partenariats" element={<PublicLayout><Partenariats /></PublicLayout>} />
                  <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
                  <Route path="/faq" element={<PublicLayout><Faq /></PublicLayout>} />
                  <Route path="/livraison-retours" element={<PublicLayout><Livraison /></PublicLayout>} />
                  <Route path="/suivi-commande" element={<PublicLayout><OrderTracking /></PublicLayout>} />
                  <Route path="/mentions-legales" element={<PublicLayout><MentionsLegales /></PublicLayout>} />
                  <Route path="/confidentialite" element={<PublicLayout><Confidentialite /></PublicLayout>} />
                  <Route path="/cookies" element={<PublicLayout><CookiesPage /></PublicLayout>} />
                  <Route path="/conditions-generales-de-vente" element={<PublicLayout><Cgv /></PublicLayout>} />
                  <Route path="/panier" element={<RetailCheckoutRoute><Cart /></RetailCheckoutRoute>} />
                  <Route path="/commande/succes" element={<RetailCheckoutRoute><CheckoutSuccess /></RetailCheckoutRoute>} />
                  <Route path="/commande/annulee" element={<RetailCheckoutRoute><CheckoutCancel /></RetailCheckoutRoute>} />
                  <Route path="*" element={<PublicLayout><NotFound /></PublicLayout>} />
                    </Routes>
                  </Suspense>
                </AdminAuthProvider>
              </WishlistProvider>
            </CartProvider>
          </B2BProvider>
        </LocaleProvider>
      </BrowserRouter>
      <Toaster position="top-center" />
    </div>
  );
}

export default App;
